import {
  EDITABLE_INSTALLMENT_STATUSES,
  Installment,
  InstallmentEditable,
} from 'components/registration/v2/types';
import { convertFromPennies } from 'core/utils';
import { DateFormat, DateService, getFormattedAmount } from 'frontend-toolkit';

export interface InstallmentFieldError {
  amount?: string;
  scheduledDate?: string;
}

export const blankInstallment: Partial<InstallmentEditable> = {
  amount: 0,
  status: 'scheduled',
};

export const dateValidations = {
  tomorrow: () =>
    DateService.format(
      DateService.addDaysToDate(1, new Date()),
      DateFormat.YYYY_MM_DD
    ),
};

export const getNewKey = () => `key-${Math.floor(Math.random() * 10000000)}`;

export const isEditableInstallment = (
  installment: Partial<Installment | InstallmentEditable>
) => EDITABLE_INSTALLMENT_STATUSES.includes(installment.status);

export const sortInstallmentsByDate = (
  a: InstallmentEditable,
  b: InstallmentEditable
) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime();

export const calculateInstallmentsPlusOne = (
  newBalance: number,
  installments: InstallmentEditable[]
) => {
  const filteredInstallments = installments.filter(isEditableInstallment); // still want to omit canceled installments
  const newInstallmentAmount = Math.ceil(
    newBalance / (filteredInstallments.length + 1)
  ); // round up here
  // Get the last scheduled installment's date
  const lastScheduledInstallmentIndex = filteredInstallments?.findLastIndex(
    (installment) => installment.status === 'scheduled'
  );
  const currentLastDate =
    installments[lastScheduledInstallmentIndex]?.scheduledDate;
  const newLastDate = currentLastDate ? new Date(currentLastDate) : new Date();
  let remainingAmount = newBalance;
  const newInstallments = filteredInstallments.map((installment) => {
    if (!isEditableInstallment(installment)) {
      return installment;
    }
    remainingAmount -= newInstallmentAmount;
    return {
      ...installment,
      _key: getNewKey(), // update key to force re-render
      amount: newInstallmentAmount,
    };
  });
  const newInstallment = {
    ...blankInstallment,
    _key: getNewKey(),
    amount: remainingAmount, // remaining amount since we rounded up
    scheduledDate: DateService.format(
      DateService.addDaysToDate(31, newLastDate),
      DateFormat.YYYY_MM_DD
    ),
  };
  newInstallments.push(newInstallment);
  return newInstallments;
};

export const recalculateInstallmentsAmounts = (
  newBalance: number,
  installments: InstallmentEditable[]
) => {
  const filteredInstallments = installments.filter(isEditableInstallment);
  const newInstallmentAmount = Math.floor(
    newBalance / filteredInstallments.length
  );
  let currentSum = 0;
  const newInstallments = filteredInstallments.map((installment, index) => {
    if (!isEditableInstallment(installment)) {
      return installment;
    }
    currentSum += newInstallmentAmount;
    let difference = 0;
    if (index === filteredInstallments.length - 1) {
      difference = newBalance - currentSum;
    }
    return {
      ...installment,
      _key: getNewKey(), // update key to force re-render
      amount: newInstallmentAmount + difference,
    };
  });

  return newInstallments;
};

export const validateInstallmentsTotal = (
  newBalance: number,
  installments: InstallmentEditable[]
): string => {
  const totalInstallmentAmount = installments.reduce((acc, installment) => {
    if (!isEditableInstallment(installment)) {
      return acc;
    }
    return acc + installment.amount;
  }, 0);
  if (totalInstallmentAmount !== newBalance) {
    return `Installments total must equal the new balance of ${getFormattedAmount(
      convertFromPennies(newBalance)
    )}, current total: ${getFormattedAmount(
      convertFromPennies(totalInstallmentAmount)
    )}`;
  }
  return '';
};

export const validateInstallments = (
  installments: InstallmentEditable[],
  min: number,
  max?: number
): Record<string, InstallmentFieldError> => {
  const errors = installments.reduce((acc, installment, index) => {
    let amountError = null;
    let dateError = null;
    // the date set on the backen should not trigger validation error (ex unpaid installments)
    const isDateFromBackend = installment.originalDate && installment.scheduledDate === installment.originalDate; 
    if (!isEditableInstallment(installment)) {
      return acc;
    }
    if (installment.amount < min) {
      amountError = `Amount must be at least ${getFormattedAmount(
        convertFromPennies(min)
      )}`;
    }
    if (max && installment.amount > max) {
      amountError = `Amount must be at most ${getFormattedAmount(
        convertFromPennies(max)
      )}`;
    }
    if (!installment.scheduledDate) {
      dateError = 'Date is required';
    }
    if (
      installment.scheduledDate &&
      !isDateFromBackend &&
      new Date(installment.scheduledDate) < new Date(dateValidations.tomorrow())
    ) {
      dateError = 'Must be 24 hours in advance';
    }
    if (installment.scheduledDate) {
      const nextInstallment =
        index < installments.length - 1 ? installments[index + 1] : undefined;
      if (
        nextInstallment &&
        nextInstallment?.status !== 'canceled' &&
        new Date(installment.scheduledDate) >
          new Date(nextInstallment.scheduledDate)
      ) {
        dateError = 'Must be before the next installment';
      }
    }
    if (amountError || dateError) {
      return {
        ...acc,
        [installment._key]: {
          amount: amountError,
          scheduledDate: dateError,
        },
      };
    }
    return acc;
  }, {} as Record<string, InstallmentFieldError>);
  return errors;
};
